import { invoke } from '@tauri-apps/api/core';

/** Cross-store native contract shared with VeeFive's billing layer. */
export type StoreProductType = 'subs' | 'inapp';

export type StoreProduct = {
  productId: string;
  title: string;
  description: string;
  productType: string;
  formattedPrice?: string;
  priceCurrencyCode?: string;
  priceAmountMicros?: number;
};

export type StorePurchase = {
  productId: string;
  purchaseToken: string;
  purchaseState: 0 | 1 | 2;
  isAutoRenewing: boolean;
  /** iOS StoreKit 2 signed transaction; Android may omit this field. */
  jwsRepresentation?: string;
  /** Apple originalTransactionId when StoreKit provides it. */
  originalId?: string;
};

export type PurchaseOptions = {
  /** A UUID generated for the authenticated ChorePop guardian. */
  appAccountToken?: string;
  /** SHA-256 of the authenticated guardian UUID; never an email or name. */
  obfuscatedAccountId?: string;
  /** Android-only, passed through for a future plan change. */
  oldPurchaseToken?: string;
};

export type IapGateway = {
  getProducts(
    productIds: string[],
    productType: StoreProductType,
  ): Promise<{ products: StoreProduct[] }>;
  purchase(
    productId: string,
    productType: StoreProductType,
    options?: PurchaseOptions,
  ): Promise<StorePurchase>;
  restorePurchases(productType: StoreProductType): Promise<{ purchases: StorePurchase[] }>;
  acknowledgePurchase(purchaseToken: string): Promise<void>;
};

function isIosWebView(): boolean {
  return typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * iOS routes through the app-owned StoreKit 2 bridge (as in VeeFive). Android
 * uses the upstream Google Play Billing bridge. Both expose the same command
 * names and neither can mint a ChorePop entitlement locally.
 */
const pluginCommand = (command: string) =>
  `plugin:${isIosWebView() ? 'chorepop-iap-ios' : 'iap'}|${command}`;

export const nativeIapGateway: IapGateway = {
  getProducts(productIds, productType) {
    return invoke(pluginCommand('get_products'), { payload: { productIds, productType } });
  },
  purchase(productId, productType, options) {
    return invoke(pluginCommand('purchase'), {
      payload: { productId, productType, ...options },
    });
  },
  restorePurchases(productType) {
    return invoke(pluginCommand('restore_purchases'), { payload: { productType } });
  },
  acknowledgePurchase(purchaseToken) {
    return invoke(pluginCommand('acknowledge_purchase'), { payload: { purchaseToken } });
  },
};
